import { MeasureController } from "../../MeasureController";
import { JudgmentResult, JudgmentResultEnum } from "../../types.client";
import { TemperatureHumidityMeasure } from "../../../models/schemas/measures/misc.schema";
import { TEMPERATURE_HUMIDITY_THRESHOLDS } from "../../constants/judgmentCriteria";

/**
 * 온습도 컨트롤러
 * 판정 기준 (TEMPERATURE_HUMIDITY_THRESHOLDS 사용):
 * - 온도: temperature.min~temperature.max (16~27℃)
 * - 습도: humidity.min~humidity.max (30~70%)
 * - 둘 다 충족해야 GOOD
 */
export class TemperatureHumidityController extends MeasureController<TemperatureHumidityMeasure> {
  protected getDefaultData(): TemperatureHumidityMeasure {
    return {
      label: "",
      temperature: 0,
      humidity: 0,
      unit: "℃/%",
      photo: { checkUrl: "", resultUrl: "" },
      result: null,
      discussion: "",
      remark: "",
      createdAt: null,
    };
  }

  protected judge(): JudgmentResult {
    const temp = this.data.temperature ?? 0;
    const humidity = this.data.humidity ?? 0;

    // 둘 다 0이면 NA
    if (temp === 0 && humidity === 0) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    // 온도 범위: TEMPERATURE_HUMIDITY_THRESHOLDS.temperature
    const { temperature: tempThreshold, humidity: humidityThreshold } =
      TEMPERATURE_HUMIDITY_THRESHOLDS;
    const tempOk = temp >= tempThreshold.min && temp <= tempThreshold.max;
    // 습도 범위: TEMPERATURE_HUMIDITY_THRESHOLDS.humidity
    const humidityOk = humidity >= humidityThreshold.min && humidity <= humidityThreshold.max;

    // 둘 다 정상이면 GOOD
    if (tempOk && humidityOk) {
      return {
        result: JudgmentResultEnum.GOOD,
        discussion: this.generateDiscussion(JudgmentResultEnum.GOOD),
      };
    }

    // 하나라도 벗어나면 BAD
    return {
      result: JudgmentResultEnum.BAD,
      discussion: this.generateDiscussion(JudgmentResultEnum.BAD),
    };
  }

  protected generateDiscussion(result: "GOOD" | "CAUTION" | "BAD" | "NA"): string {
    const temp = this.data.temperature ?? 0;
    const humidity = this.data.humidity ?? 0;

    if (result === JudgmentResultEnum.NA) {
      return "온도 및 습도 측정값이 입력되지 않았습니다.";
    }

    const { temperature: tempThreshold, humidity: humidityThreshold } =
      TEMPERATURE_HUMIDITY_THRESHOLDS;
    const tempOk = temp >= tempThreshold.min && temp <= tempThreshold.max;
    const humidityOk = humidity >= humidityThreshold.min && humidity <= humidityThreshold.max;

    if (result === JudgmentResultEnum.GOOD) {
      return `온도 ${temp}℃, 습도 ${humidity}%로 정상 범위(온도 ${tempThreshold.min}~${tempThreshold.max}℃, 습도 ${humidityThreshold.min}~${humidityThreshold.max}%)입니다.`;
    }

    const issues: string[] = [];
    if (!tempOk) {
      issues.push(`온도 ${temp}℃는 기준(${tempThreshold.min}~${tempThreshold.max}℃) 범위 밖`);
    }
    if (!humidityOk) {
      issues.push(
        `습도 ${humidity}%는 기준(${humidityThreshold.min}~${humidityThreshold.max}%) 범위 밖`
      );
    }

    return issues.join(", ") + "입니다.";
  }
}
